import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import TextField from '@shared/ui/atoms/TextField';
import { canUserEditOfficesInManagement } from '@shared/utils/AccessControl';
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { SpaceBetweenRow } from "../../StyleComponents";
import { createOrUpdateOfficeLayout } from '@features/desks/store/editOfficeLayout.duck';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { SecondaryButton } from '@shared/ui/atoms/buttons/SecondaryButton';
import { useTranslation } from "react-i18next";

export function OfficeInformationPanel(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {office} = props;
  const currentUser = useSelector(selectCurrentUser);
  const isEditable = useMemo(() => canUserEditOfficesInManagement(currentUser), [currentUser]);
  const [officeName, setOfficeName] = useState('');
  const [officePostcode, setOfficePostcode] = useState('');
  const [threeLetterCode, setThreeLetterCode] = useState('');

  useEffect(() => {
    return () => {
      setOfficeName('');
      setOfficePostcode('');
      setThreeLetterCode('');
    }
  }, [])

  useEffect(() => {
    if (props.office) {
      setOfficeName(props.office.label);
      setOfficePostcode(props.office.postCode);
      setThreeLetterCode(props.office.threeLetterCode);
    }
  }, [props.office]);

  const onSaveClicked = async () => {
    dispatch(createOrUpdateOfficeLayout({
      ...(office || {}),
      label: officeName,
      postCode: officePostcode,
      threeLetterCode: threeLetterCode,
    }));
  }

  const onDeleteClicked = async () => {
    if (office) {
      dispatch(openDialogWithPayload({
        activeDialog: DialogIdentifiers.DeleteOfficeEntityDialog,
        payload: { officeId: office.id },
      }));
    }
  }

  return (
    <Container>
      <HeadlineMedium style={{marginBottom: 24}}>{t('settings.office-information')}</HeadlineMedium>
      <FlatContentCard style={{marginBottom: 42}}>
        <BodyRegular weight={600}>{t('general.how-it-works')}</BodyRegular>
        <BodyRegular>{t('settings.office-information-how-it-works')}</BodyRegular>
      </FlatContentCard>
      <Content>
        <TextField disabled={!isEditable}
                   onChange={setOfficeName}
                   label="textinput.office-name"
                   value={officeName}
                   fullWidth={true}
        />
        <TextField disabled={!isEditable}
                   onChange={setOfficePostcode}
                   label="textinput.office-postcode"
                   value={officePostcode}
                   fullWidth={true}
        />
        <TextField disabled={!isEditable}
                   onChange={setThreeLetterCode}
                   label="textinput.office-three-letter-code"
                   value={threeLetterCode}
                   fullWidth={true}
        />
      </Content>
      <SpaceBetweenRow>
        <PrimaryButton click={onSaveClicked} text={'button.save-changes'} />
        {office && <SecondaryButton click={onDeleteClicked} text={'button.delete-office'} />}
      </SpaceBetweenRow>
    </Container>
  )
}

interface Props {
  office?: OfficeEntity;
}

const Container = styled.div`
`

const Content = styled.div`
  max-width: 400px;
`
